import { CondoType, getBlockLabel, getUnitLabel } from './terminology';

/**
 * A vehicle's unit, written the way a porteiro needs to read it.
 *
 * The Veículos card, the detail modal and the edit form each hand-built
 * `${blockLabel} ${block} — ${unitLabel} ${number}`, so none of them showed the STREET — even
 * though the API preloads `Street` on both `unit` and `owner_unit`. In a loteamento the street
 * is the half you actually direct somebody by.
 *
 * Reads outside-in and drops nothing: quadra, then street, then lote. `formatUnitLabel` drops
 * the quadra as soon as a street exists, which is wrong here — a street alone does not say
 * which quadra, and both are needed to find the place.
 */

export interface VehicleUnitLike {
  id?: string;
  number?: string | null;
  block?: { id?: string; name?: string } | null;
  street?: { id?: string; name?: string } | null;
  street_number?: string | null;
}

export interface VehicleUnitLines {
  /** "Rua das Acácias, 100" — how you get to the place. Null when no street is recorded. */
  street: string | null;
  /** "Quadra 17 – Lote 6" — how you find it once you are there. */
  unit: string | null;
}

/**
 * The same facts as [vehicleUnitLabel], split into the two rows every surface renders.
 *
 * One run-on line did not fit the card or the modal's 290px column and truncated mid-word,
 * and the three surfaces each split it differently — or, in the edit form, not at all.
 */
export function vehicleUnitLines(
  unit: VehicleUnitLike | null | undefined,
  condoType: CondoType,
): VehicleUnitLines {
  if (!unit) return { street: null, unit: null };

  const streetName = unit.street?.name?.trim();
  const streetNumber = (unit.street_number ?? '').trim();
  const street = streetName
    ? (streetNumber ? `${streetName}, ${streetNumber}` : streetName)
    : null;

  const parts: string[] = [];
  if (unit.block?.name) parts.push(`${getBlockLabel(condoType)} ${unit.block.name}`);
  const number = (unit.number ?? '').trim();
  if (number) parts.push(`${getUnitLabel(condoType)} ${number}`);

  return { street, unit: parts.length ? parts.join(' – ') : null };
}

export function vehicleUnitLabel(
  unit: VehicleUnitLike | null | undefined,
  condoType: CondoType,
): string | null {
  if (!unit) return null;

  const parts: string[] = [];

  if (unit.block?.name) parts.push(`${getBlockLabel(condoType)} ${unit.block.name}`);

  const streetName = unit.street?.name?.trim();
  if (streetName) {
    const number = (unit.street_number ?? '').trim();
    parts.push(number ? `${streetName}, ${number}` : streetName);
  }

  const number = (unit.number ?? '').trim();
  if (number) parts.push(`${getUnitLabel(condoType)} ${number}`);

  return parts.length ? parts.join(' – ') : null;
}

/**
 * The unit a vehicle's address should come from.
 *
 * `unit` is the vehicle's OWN registration and outranks everything else. A person-owned
 * vehicle has none — its address is the owner's, which the API supplies as `owner_unit`.
 * Reading `unit` alone is why the Veículos edit form came up "— Todos —" / "— Nenhuma —" for
 * a morador's car, and why the detail modal showed no address at all.
 */
export function vehicleAddressUnit(
  vehicle: { unit?: VehicleUnitLike | null; owner_unit?: VehicleUnitLike | null } | null | undefined,
): VehicleUnitLike | null {
  if (!vehicle) return null;
  if (vehicle.unit?.id) return vehicle.unit;
  if (vehicle.owner_unit?.id) return vehicle.owner_unit;
  return null;
}
